import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '@/api/axios';
import {
  extractTokenFromUrl,
  storeAuthData,
  getStoredToken,
  clearAuthData,
  getStoredUser,
  setStoredUser,
} from '@/utils/auth';
import { parseIframeParams, type IframeParams } from '@/lib/iframe-params';
import { verifyBet99Token, computeSsoUserId, type JWTPayload } from '@/lib/jwt-verify';
import { playBoxClient, type LoginStatusResponse } from '@/lib/play-box-client';
import { useUpdateUsername } from '@/api/mutations/useUpdateUsername';

interface AuthContextType {
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: Error | null;
  login: (token: string) => Promise<void>;
  logout: () => void;
  user: any | null;
  iframeParams: IframeParams;
  jwtPayload: JWTPayload | null;
  ssoUserId: string | null;
  username: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [iframeParams, setIframeParams] = useState<IframeParams>({});
  const [jwtPayload, setJwtPayload] = useState<JWTPayload | null>(null);
  const [ssoUserId, setSsoUserId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const updateUsernameMutation = useUpdateUsername();

  // Login function that exchanges token for JWT
  const login = async (token: string, location?: string) => {
    console.log('[AUTH] [AuthContext] ========================================');
    console.log('[AUTH] [AuthContext]  LOGIN FUNCTION CALLED');
    console.log('[AUTH] [AuthContext] ========================================');
    try {
      console.log('[AUTH] [AuthContext]  Starting login with token:', token);
      console.log('[AUTH] [AuthContext]  Token length:', token?.length);
      console.log('[AUTH] [AuthContext]  Token preview:', token?.substring(0, 20));
      console.log('[AUTH] [AuthContext]  Location parameter received:', location);
      console.log('[AUTH] [AuthContext]  Setting isLoading to true...');
      setIsLoading(true);

      // Clear any previous tokens before making the login request
      console.log('[AUTH] [AuthContext]  Clearing previous auth tokens...');
      clearAuthData();

      // Call backend login endpoint with token and location
      console.log('[AUTH] [AuthContext]  Preparing to call POST /api/login');
      console.log('[AUTH] [AuthContext]  Request payload:', {
        token: token?.substring(0, 20) + '...',
        location,
      });
      console.log('[AUTH] [AuthContext]  Making API request NOW...');
      const response = await api.post('/api/login', { token, location });
      console.log('[AUTH] [AuthContext]  API request completed');

      console.log('[AUTH] [AuthContext]  Login response received');
      console.log('[AUTH] [AuthContext]  Response data:', response.data);

      // Backend returns {token, user_id, sso_user_id, ...} NOT {token, user: {...}}
      // So we need to construct the user object from the response
      const { token: auth_token, user_id, sso_user_id, ...rest } = response.data;

      console.log('[AUTH] [AuthContext]  Extracted from response:', {
        auth_token: auth_token?.substring(0, 30) + '...',
        user_id,
        sso_user_id,
        rest,
      });

      // Construct user object
      const userData = {
        id: user_id || sso_user_id,
        user_id,
        sso_user_id,
        location,
        ...rest,
      };

      console.log('[AUTH] [AuthContext]  Constructed user object:', userData);
      console.log('[AUTH] [AuthContext]  Location in user object:', userData.location);

      // Merge location from URL with backend user data to ensure it's always preserved
      console.log('[AUTH] [AuthContext]  Merging location...');
      console.log('[AUTH] [AuthContext]  location parameter:', location);
      const userWithLocation = {
        ...userData,
        location: location || userData.location, // Prefer URL location over backend
      };
      console.log('[AUTH] [AuthContext]  Merged user object:', userWithLocation);
      console.log('[AuthContext.login]  Final location in merged user:', userWithLocation.location);

      // Store auth data using utilities
      console.log('[AUTH] [AuthContext]  Storing auth data with location...');
      storeAuthData({
        token: auth_token,
        user: userWithLocation,
        refreshToken: response.data.refreshToken,
      });

      // Update state with merged data
      console.log('[AUTH] [AuthContext]  Setting state...');
      setAuthToken(auth_token);
      setUser(userWithLocation);
      setIsAuthenticated(true);
      setAuthError(null); // Clear any previous errors
      setIsLoading(false); // Only set loading to false on successful login
      console.log('[AUTH] [AuthContext]  Login successful! Final user state:', userWithLocation);
      console.log('[AUTH] [AuthContext]  Location in final state:', userWithLocation.location);
    } catch (error) {
      console.error('[AuthContext.login]  Login failed:', error);
      console.error('[AuthContext.login]  Location parameter was:', location);

      // Set authentication error and keep loading state active
      const authError = error instanceof Error ? error : new Error('Authentication failed');
      setAuthError(authError);

      // Don't set isLoading=false - keep loading screen visible
      // Don't clear isAuthenticated - user might have previous session

      throw error;
    }
  };

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    clearAuthData();
  };

  // Single useEffect to handle all auth initialization
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AuthContext] ========================================');
      console.log('[AuthContext]  STARTING AUTH INITIALIZATION');
      console.log('[AuthContext] ========================================');

      const params = parseIframeParams();
      setIframeParams(params);
      console.log('[AuthContext] 📋 Parsed iframe params:', params);

      // Extract location from URL params - this is available immediately
      const locationFromUrl = params.location as 'ca' | 'ca-on' | undefined;
      console.log('[AuthContext]  Location extracted from URL params:', locationFromUrl);

      // Check if we're in an iframe
      const isInIframe = window.self !== window.top;
      console.log('[AuthContext] 🖼️ Running in iframe:', isInIframe);

      // Get REAL token from URL (without COERCE fallback)
      const urlParams = new URLSearchParams(window.location.search);
      const realTokenFromUrl = urlParams.get('token');
      console.log('[AuthContext]  Real token in URL:', realTokenFromUrl ? 'Yes' : 'No');
      console.log('[AuthContext]  Real token value:', realTokenFromUrl);
      console.log('[AuthContext]  params.token value:', params.token);

      // PRIORITY 1: If we're in iframe with no URL token, use play-box authentication
      console.log('[AuthContext]  Checking play-box auth condition:', {
        isInIframe,
        realTokenFromUrl,
        shouldUsePlayBox: isInIframe && !realTokenFromUrl,
      });

      if (isInIframe && !realTokenFromUrl) {
        console.log('[AUTH] [AuthContext] ENTERING PLAY-BOX AUTH FLOW');
        console.log(
          '[AUTH] [AuthContext] Iframe with no URL token - requesting auth from play-box...'
        );
        try {
          console.log('[AUTH] [AuthContext] Calling playBoxClient.getLoginStatus()...');
          const loginStatus = await new Promise<LoginStatusResponse>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
              console.error('[AUTH] [AuthContext] Play-box auth request TIMEOUT (10 seconds)');
              reject(new Error('Play-box auth request timeout'));
            }, 10000);

            playBoxClient.getLoginStatus(
              response => {
                console.log(
                  '[AUTH] [AuthContext] Play-box callback invoked with response:',
                  response
                );
                clearTimeout(timeoutId);
                resolve(response);
              },
              () => {
                console.error(
                  '[AUTH] [AuthContext] Play-box getLoginStatus timeout callback invoked'
                );
                clearTimeout(timeoutId);
                reject(new Error('Play-box getLoginStatus timeout'));
              }
            );
          });

          console.log('[AUTH] [AuthContext] Play-box auth response:', {
            LoginStatus: loginStatus.LoginStatus,
            userId: loginStatus.userId || loginStatus.SessionId,
            userIdType: typeof loginStatus.userId,
            hasToken: !!loginStatus.token,
          });

          if (loginStatus.token) {
            console.log('[AUTH] [AuthContext] Token found in play-box response');
            console.log('[AUTH] [AuthContext] Token characteristics:', {
              tokenPrefix: loginStatus.token.substring(0, 20),
              isTestToken:
                loginStatus.token.startsWith('test-') || loginStatus.token.startsWith('mock-'),
            });
            console.log('[AUTH] [AuthContext] Calling /api/login with token');
            console.log('[AUTH] [AuthContext] Location parameter:', locationFromUrl);

            await login(loginStatus.token, locationFromUrl);

            console.log('[AuthContext]  /api/login completed successfully');
            console.log('[AuthContext]  Login successful with play-box token');
          } else {
            console.log('[AuthContext]  No token in play-box response - skipping login');
            setAuthError(new Error('No authentication token provided'));
            // Don't set isLoading=false - keep loading screen visible
          }
          console.log('[AuthContext] 🏁 Exiting play-box auth flow (return)');
          return; // Exit early - play-box flow complete
        } catch (error) {
          console.error('[AuthContext]  Failed to get auth from play-box:', error);
          console.error('[AuthContext]  Error details:', {
            message: (error as Error).message,
            stack: (error as Error).stack,
          });
          setAuthError(
            error instanceof Error ? error : new Error('Play-box authentication failed')
          );
          // Don't set isLoading=false - keep loading screen visible with error
          console.log('[AuthContext] 🏁 Exiting play-box auth flow on error (return)');
          return; // Exit early on error
        }
      } else {
        console.log('[AuthContext] ⏭️ SKIPPING play-box auth flow');
        console.log('[AuthContext] ⏭️ Reason:', !isInIframe ? 'Not in iframe' : 'Token in URL');
      }

      // PRIORITY 2: Check for SSO JWT token in URL params (legacy SSO flow)
      if (params.token) {
        console.log('[AuthContext] Found SSO token in iframe params, verifying...');
        const payload = await verifyBet99Token(params.token);
        setJwtPayload(payload);

        if (payload) {
          console.log('[AuthContext] SSO JWT verified successfully');
          const computedSsoUserId = computeSsoUserId(payload);
          setSsoUserId(computedSsoUserId);

          // Note: Token is read from URL on every API request - no storage needed
          console.log('[AuthContext] Token available in URL, no storage required');
          setAuthToken(params.token);
          setIsAuthenticated(true);

          // Create base user object with location (always needed for API calls)
          console.log('[AuthContext] 🏗️ Creating base user object with location...');
          const userObj = {
            id: computedSsoUserId,
            username: `user_${computedSsoUserId}`,
            email: `${payload.sub_id.uid}@bet99.com`,
            location: locationFromUrl,
          };
          console.log('[AuthContext]  Base userObj created:', userObj);
          console.log('[AuthContext]  Location in userObj:', userObj.location);

          // Set user state IMMEDIATELY so UserContext can sync it
          // This prevents a double-fetch where hooks fetch without location first
          console.log('[AuthContext] ⚡ Setting user state IMMEDIATELY with location');
          setUser(userObj);
          setStoredUser(userObj);

          // Call login endpoint with token and location to create/update user (non-blocking)
          try {
            console.log('[AuthContext]  Calling backend /login with token and location...');
            console.log('[AuthContext]  Passing location to login():', locationFromUrl);
            await login(params.token, locationFromUrl);
            console.log(
              '[AuthContext]  Backend login successful - user created/updated with location'
            );
          } catch (loginError) {
            console.error('[AuthContext]  Backend login failed:', loginError);
            console.log('[AuthContext]  Location when login failed:', locationFromUrl);
            // Set error and keep loading - authentication failed
            setAuthError(
              loginError instanceof Error ? loginError : new Error('Backend login failed')
            );
            // Don't continue - user must successfully authenticate
            return;
          }

          // Username update happens separately via Play-box (non-blocking)
          try {
            console.log('[AuthContext]  Requesting login status from host via Play-box...');
            const loginStatus = await new Promise<LoginStatusResponse>((resolve, reject) => {
              playBoxClient.getLoginStatus(
                response => resolve(response),
                () => reject(new Error('Play-box getLoginStatus timeout'))
              );
            });
            console.log('[AuthContext]  Play-box login status received:', {
              LoginStatus: loginStatus.LoginStatus,
              Username: loginStatus.Username,
              SessionId: loginStatus.SessionId,
              fullResponse: loginStatus,
            });

            console.log('[AuthContext]  Checking username update trigger conditions:', {
              'loginStatus.LoginStatus': loginStatus.LoginStatus,
              'loginStatus.LoginStatus === 2': loginStatus.LoginStatus === 2,
              'loginStatus.Username': loginStatus.Username,
              hasUsername: !!loginStatus.Username,
              shouldUpdate: loginStatus.LoginStatus === 2 && !!loginStatus.Username,
            });

            // Update username if user is logged in
            if (loginStatus.LoginStatus === 2 && loginStatus.Username) {
              console.log('[AuthContext]  Conditions met - proceeding with username update');
              console.log(
                '[AuthContext] 📝 Setting username in local state:',
                loginStatus.Username
              );
              setUsername(loginStatus.Username);
              console.log('[AuthContext] 📝 Setting username in userObj:', loginStatus.Username);
              userObj.username = loginStatus.Username;

              // Persist username to backend database
              try {
                console.log('[AuthContext]  Calling backend API to update username...');
                console.log('[AuthContext]  Mutation payload:', {
                  username: loginStatus.Username,
                });
                await updateUsernameMutation.mutateAsync({ username: loginStatus.Username });
                console.log('[AuthContext]  Username successfully updated in backend database');
              } catch (updateError) {
                console.error('[AuthContext]  Failed to update username in backend:', updateError);
                console.error('[AuthContext]  Error details:', {
                  message: updateError?.message,
                  response: updateError?.response,
                  error: updateError,
                });
              }
            } else {
              console.log('[AuthContext]  Username update conditions NOT met - skipping update', {
                reason: !loginStatus.LoginStatus
                  ? 'No LoginStatus'
                  : loginStatus.LoginStatus !== 2
                    ? `LoginStatus is ${loginStatus.LoginStatus} (expected 2)`
                    : !loginStatus.Username
                      ? 'No Username in response'
                      : 'Unknown reason',
              });
            }
          } catch (error) {
            console.error('[AuthContext]  Failed to get login status from Bridge API:', error);
            console.error('[AuthContext]  Error details:', {
              message: error?.message,
              error: error,
            });
          }

          // Always set user object with location
          console.log('[AuthContext]  Setting final user state (after login attempt)...');
          console.log('[AuthContext]  Final userObj being set:', userObj);
          console.log('[AuthContext]  Location in final userObj:', userObj.location);

          setUser(userObj);
          setStoredUser(userObj);

          setIsLoading(false);
          console.log('[AuthContext]  SSO authentication complete');
          console.log('[AuthContext]  Final location in state should be:', userObj.location);
          return;
        } else {
          console.error('[AuthContext] SSO JWT verification failed');
        }
      }

      const tokenFromQuery = extractTokenFromUrl();
      console.log('[AuthContext] Token from URL (legacy):', tokenFromQuery);

      if (tokenFromQuery) {
        console.log('[AuthContext] Attempting legacy login with token from URL');
        try {
          await login(tokenFromQuery, locationFromUrl);
        } catch (error) {
          console.error('[AuthContext] Login failed:', error);
          setAuthError(error instanceof Error ? error : new Error('Legacy login failed'));
          // Don't set isLoading=false - keep loading screen visible with error
        }
      } else {
        // No token in URL - check stored token first
        const storedToken = getStoredToken();
        const storedUser = getStoredUser();
        console.log('[AuthContext] Stored legacy token:', storedToken);

        if (storedToken) {
          setAuthToken(storedToken);
          const updatedUser = {
            ...storedUser,
            location: locationFromUrl,
          };
          setUser(updatedUser);
          setIsAuthenticated(true);
          setAuthError(null);
          setIsLoading(false);
        } else {
          // No token anywhere - not in iframe or play-box not available
          console.log('[AuthContext] No token found and not in iframe context');
          setAuthError(new Error('No authentication token found'));
          // Don't set isLoading=false - keep loading screen visible with error
        }
      }
    };

    initializeAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authToken,
        isAuthenticated,
        isLoading,
        authError,
        login,
        logout,
        user,
        iframeParams,
        jwtPayload,
        ssoUserId,
        username,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
